import React, { useContext, useEffect, useRef } from "react";
import * as THREE from "three";
import { ThemeContext } from "../context/ThemeContext";

const WebWhaleScene = ({ className = "" }) => {
  const mountRef = useRef(null);
  const { dark } = useContext(ThemeContext);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, 1, 0.1, 100);
    camera.position.z = 7;
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.6));
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const count = 650;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const colorA = new THREE.Color(dark ? "#8b7cff" : "#6254e8");
    const colorB = new THREE.Color(dark ? "#31d8ff" : "#159dcc");

    for (let i = 0; i < count; i += 1) {
      const radius = 2.4 + Math.random() * 2.5;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = radius * Math.cos(phi);
      const mixed = colorA.clone().lerp(colorB, Math.random());
      colors[i * 3] = mixed.r; colors[i * 3 + 1] = mixed.g; colors[i * 3 + 2] = mixed.b;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    const material = new THREE.PointsMaterial({ size: dark ? 0.035 : 0.025, transparent: true, opacity: dark ? 0.72 : 0.42, vertexColors: true, blending: THREE.AdditiveBlending, depthWrite: false });
    const points = new THREE.Points(geometry, material);
    scene.add(points);

    const ringGeometry = new THREE.TorusGeometry(2.15, 0.008, 8, 160);
    const ringMaterial = new THREE.MeshBasicMaterial({ color: dark ? 0x6d5dfc : 0x6254e8, transparent: true, opacity: dark ? 0.18 : 0.12 });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.rotation.x = Math.PI * 0.62;
    scene.add(ring);

    const resize = () => {
      const width = mount.clientWidth || 500;
      const height = mount.clientHeight || 500;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height, false);
    };
    resize();
    window.addEventListener("resize", resize);

    let frame = 0;
    const animate = () => {
      frame = requestAnimationFrame(animate);
      points.rotation.y += 0.00055;
      points.rotation.x += 0.00018;
      ring.rotation.z -= 0.00035;
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
      geometry.dispose(); material.dispose(); ringGeometry.dispose(); ringMaterial.dispose(); renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, [dark]);

  return <div ref={mountRef} className={className} aria-hidden="true" />;
};

export default WebWhaleScene;